import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import type { ContractListResponse, ContractResponse } from '@timelog/pm-shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFinancials } from './editor';

import { QueryKeys, useApiClient } from '@/api';

/**
 * Explicit 409 resolution for the contract aggregate (BR-20): "Keep mine"
 * re-PUTs the local document with the fresh version (overwriting the other
 * edit); "Take theirs" discards local changes. No silent merge in v1.
 */
export const ContractConflictDialog = () => {
  const { state, dispatch, project } = useFinancials();
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation('pmFinancials');

  const open = state.saveStatus === 'conflict';

  const fetchFresh = async (): Promise<ContractResponse> => {
    const fresh = await api.get<ContractResponse>(
      `/projects/${project.id}/contracts/${state.contractId}`,
    );
    queryClient.setQueryData(
      QueryKeys.ProjectContracts(project.id),
      (list: ContractListResponse | undefined) =>
        list && {
          contracts: list.contracts.map((contract) =>
            contract.id === fresh.id ? fresh : contract,
          ),
        },
    );
    return fresh;
  };

  const keepMine = async () => {
    setBusy(true);
    try {
      const fresh = await fetchFresh();
      dispatch({ type: 'RESOLVE_CONFLICT_KEEP_MINE', version: fresh.version });
    } finally {
      setBusy(false);
    }
  };

  const takeTheirs = async () => {
    setBusy(true);
    try {
      const fresh = await fetchFresh();
      dispatch({ type: 'ADOPT_SERVER', contract: fresh });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} data-automation-id="conflict-dialog" maxWidth="xs" fullWidth>
      <DialogTitle>{t('conflictDialog.Title')}</DialogTitle>
      <DialogContent>
        <Typography variant="bodyRegular" color="text.secondary">
          {t('conflictDialog.Body')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => void takeTheirs()}
          disabled={busy}
          color="inherit"
          data-automation-id="conflict-take-theirs"
        >
          {t('conflictDialog.Discard')}
        </Button>
        <Button
          onClick={() => void keepMine()}
          disabled={busy}
          variant="contained"
          data-automation-id="conflict-keep-mine"
        >
          {t('conflictDialog.Keep')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
